/**
 * RecordHost — the §F8 dataset record, drawn wherever the reader is (client item 6).
 *
 * The record used to belong to `/catalogue`: the page held the selected id in its own
 * `useState` and rendered `DatasetRecord` as a child, so "View metadata" on a layer row in
 * the map's §F5.2 sidebar had nowhere to send the reader but the catalogue — a navigation
 * that unmounts the map and loses everything on it. Hoisting the id into the store
 * (`recordId`) and the modal into one app-level host makes the record an *overlay*: the
 * same shape as `SplashLanding` and `SignInGate`, a sibling of the routes rather than one
 * of them. No route moves, no map state is touched, and closing it puts the reader back on
 * the page they never left.
 *
 * `domainNames` comes off the same `/datasets` envelope the catalogue's own cards read
 * (`facets.domain`, via `domainFacetsById`), so a record opened from the map names its
 * domain exactly as a record opened from a card does. The query is `enabled` only while a
 * record is open: a reader who never asks for one never pays for the catalogue fetch, and
 * a reader coming from `/catalogue` finds it already in the cache.
 *
 * **Any `['datasets', …]` entry will do** (client round 3). Since the "Data source" facet
 * began narrowing on the server, `/catalogue` can be holding `['datasets', lang, 'ISRIC']`
 * and nothing under the whole-catalogue key — and asking for the whole catalogue here would
 * be a second round trip for a block that is *identical in every entry*: `facets` always
 * describes the whole catalogue, whatever `?source=` narrowed `results` to. So the host
 * reads `facets.domain` off whichever catalogue entry the cache already has, and only
 * fetches when it has none.
 */
import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchDatasets, queryKeys, type DatasetsResponse } from '../lib/api';
import { useAppStore } from '../lib/state';
import { domainFacetsById } from '../features/catalogue/catalogue';
import { DatasetRecord } from '../features/catalogue/DatasetRecord';

export function RecordHost() {
  const recordId = useAppStore((s) => s.recordId);
  const closeRecord = useAppStore((s) => s.closeRecord);
  const queryClient = useQueryClient();

  /**
   * A catalogue envelope already in the cache under *any* `source`, or `undefined`. Read on
   * render rather than subscribed to: it only decides whether the fetch below is needed, and
   * that fetch's own `data` is what re-renders the host when it lands.
   */
  const cached = queryClient
    .getQueriesData<DatasetsResponse>({ queryKey: ['datasets'] })
    .find(([, body]) => body?.facets?.domain != null)?.[1];

  const { data } = useQuery({
    queryKey: queryKeys.datasets(),
    queryFn: fetchDatasets,
    enabled: recordId != null && cached == null,
  });

  const facets = cached ?? data;
  const domainNames = useMemo(() => domainFacetsById(facets?.facets.domain), [facets]);

  if (recordId == null) return null;
  return <DatasetRecord id={recordId} onClose={closeRecord} domainNames={domainNames} />;
}

export default RecordHost;
